import {
	Calendar,
	CalendarIcon,
	Code,
	HeartHandshake,
	Lightbulb,
	Mail,
	MapPin,
	Users,
	Users2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { GithubIcon } from "@/components/ui/github-icon";
import { Event, type EventProps } from "@/components/event";
import type { MemberProps } from "@/components/member";
import { Member } from "@/components/member";
import type { SlideShowItem } from "@/components/slideshow";
import { Slideshow } from "@/components/slideshow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import {
	Card,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";

const PAST_EVENTS_PREVIEW_COUNT = 2;
const PAST_EVENTS_OVERLAY_RANGE_MS = 365 * 24 * 60 * 60 * 1000;

function eventsSection(isLoading: boolean, error: string | null, events: EventProps[]): React.ReactElement {
	const now = Date.now();

	// Conditional rendering for loading, error, and no events
	if (isLoading) {
		return <div className="text-center py-20">Loading events...</div>;
	}

	if (error) {
		return <div className="text-center py-20 text-red-500">Error: Could not load events.</div>;
	}

	if (events.length === 0) {
		return <div className="text-center py-20 dark:text-gray-300">No scheduled events found.</div>;
	}

	// Most recent past event first
	const pastEvents = events
		.filter((event) => event.endTimestamp < now)
		.sort((a, b) => b.startTimestamp - a.startTimestamp);
	const recentPastEvents = pastEvents.slice(0, PAST_EVENTS_PREVIEW_COUNT);
	const pastYearEvents = pastEvents.filter(
		(event) => event.startTimestamp >= now - PAST_EVENTS_OVERLAY_RANGE_MS,
	);
	const hasMorePastEvents = pastEvents.length > PAST_EVENTS_PREVIEW_COUNT;

	const futureEvents = events.filter((event) => event.endTimestamp >= now);

	return <>
		{/* Past Events */}
		<div className="space-y-4">
			<h3 className="text-xl font-semibold dark:text-white text-gray-900 mb-4 flex items-center">
				<Users2 className="w-5 h-5 mr-2 dark:text-blue-400 text-blue-500" />
				Vergangene Treffen
			</h3>
			{recentPastEvents.map((event) => (
				<Event key={event.title + event.startTimestamp} {...event} />
			))}
			{hasMorePastEvents && (
				<Dialog>
					<DialogTrigger asChild>
						<Button className="w-full" variant="outline">
							Weitere vergangene Events anzeigen
						</Button>
					</DialogTrigger>
					<DialogContent className="max-h-[80vh] overflow-y-auto">
						<DialogHeader>
							<DialogTitle>Vergangene Events (letztes Jahr)</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							{pastYearEvents.map((event) => (
								<Event key={event.title + event.startTimestamp} {...event} />
							))}
						</div>
					</DialogContent>
				</Dialog>
			)}
		</div>

		{/* Future Events */}
		<div className="space-y-4">
			<h3 className="text-xl font-semibold dark:text-white text-gray-900 mb-4 flex items-center">
				<CalendarIcon className="w-5 h-5 mr-2 text-blue-400" />
				Kommende Events
			</h3>
			{futureEvents.map((event) => (
				<Event key={event.title + event.startTimestamp} {...event} />
			))}
		</div>
	</>
}

export default function HomePage() {
	// Define the URL for the JSON data
	const eventsUrl = "https://raw.githubusercontent.com/TUM-Dev/Website/refs/heads/event-data/scheduled_events.json";

	// Use state to manage the events data and a loading state
	const [events, setEvents] = useState<EventProps[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const members: readonly MemberProps[] = [
		{
			avatar: "/avatars/jakob.webp",
			github: "jakobkoerber",
			leads_project: "Campus App",
			linkedin: "jakobkoerber",
			name: "Jakob Körber",
			role: "Mobile Lead",
		},
		{
			avatar: "/avatars/joscha.webp",
			github: "joschahenningsen",
			leads_project: "Tum.live & tum.sexy",
			linkedin: "joscha-henningsen-27728417b",
			name: "Joscha Henningsen",
			role: "Backend Developer",
		},
		{
			avatar: "/avatars/frank.webp",
			github: "CommanderStorm",
			leads_project: "NavigaTUM",
			linkedin: "frankelsinga",
			name: "Frank Elsinga",
			role: "GeoInformatics Lead",
		},
		{
			avatar: "/avatars/florian.webp",
			github: "FlorianDev",
			leads_project: "Vorstand",
			linkedin: "florian-schmidt-1965721b2",
			name: "Florian Schmidt",
			role: "Communication Lead",
		},
		{
			avatar: "/avatars/fabian.webp",
			github: "com8",
			leads_project: "Thesis template",
			name: "Fabian Sauter",
			role: "Latex Expert",
		},
		{
			avatar: "/avatars/placeholder.webp",
			name: "Rishab Garg",
			role: "Backend Developer",
		},
		{
			avatar: "/avatars/kordian.webp",
			github: "kordianbruck",
			linkedin: "kordianbruck",
			name: "Kordian Bruck",
			role: "Lead & Sponsor",
		},
		{
			avatar: "/avatars/benjamin.webp",
			github: "Bentipa",
			linkedin: "benjamin-pabst",
			name: "Benjamin Pabst",
			role: "Campus App",
		},
		{
			avatar: "/avatars/gabriel.webp",
			github: "Friendly-Banana",
			leads_project: "Eat-api",
			name: "Gabriel",
			role: "Backend Developer",
		},
	];
	const slideshow: readonly SlideShowItem[] = [
		{
			alt: "Monatliches Team Meeting",
			caption:
				"Einmal im monat machen wir ein Team Meeting, wo wir Ideen/Probleme besprechen und unseren Fortschritt präsentieren",
			src: "/events/tunix25.webp",
		},
		{
			alt: "Team Events",
			caption:
				"Hin und wieder treffen wir uns für gemeinsame Aktivitäten - hier beispielsweise beim GarNix",
			src: "/events/garnix25.webp",
		},
		//{
		//    src: "/events/hackatum.webp",
		//    alt: "Team bei einem Hackathon",
		//    caption: "48h Hackathon - Intensive Coding-Sessions mit Pizza und viel Kaffee",
		//},
		{
			alt: "Team beim Server-Aufbau",
			caption: "Hands-on: Gemeinsamer Aufbau von neuen Live-Streaming Servern",
			src: "/events/server-bau24.webp",
		},
		{
			alt: "Livestreaming setup für eine ",
			caption:
				"Hin und wieder machen wir LiveStreaming für andere Hochschulgruppen - Hier beispielsweise für den BayStuRa",
			src: "/events/live-stream-baystura24.webp",
		},
		{
			alt: "Team auf einer Konferenz",
			caption:
				"Gemeinsam auf einer Open Source Konferenz - Networking und neue Ideen",
			src: "/events/fossgis25.webp",
		},
		{
			alt: "Team Dinner",
			caption: "Team Dinner - Auch abseits des Codes verstehen wir uns super",
			src: "/events/mc-donalds24.webp",
		},
		{
			alt: "TUM-Projekt Präsentation",
			caption:
				"TUM-Präsentationen - Hin und wieder präsentieren wir IT-Projekte. Hier beispielsweise mit unserem CIO beim iPraktikum",
			src: "/events/ipraktikum23.webp",
		},
		{
			alt: "Projekt Präsentation",
			caption:
				"Projekt-Präsentationen - Wir präsentieren regelmäßig unsere projekte bei diversen veranstaltungen. Hier die StudentClub Fair",
			src: "/events/student-club-fair24.webp",
		},
		{
			alt: "Projekt Präsentation",
			caption:
				"Projekt-Präsentationen - Wir präsentieren regelmäßig unsere projekte bei diversen veranstaltungen. Hier die StudentClub Fair",
			src: "/events/student-club-fair25.webp",
		},
		{
			alt: "Menschen vor einer Photobox, die sie gebaut haben",
			caption:
				"Neben code machen wir auch Veranstaltungen - hier haben wir beispielsweise die Photobox für die Unity gebaut",
			src: "/events/photobox25.webp",
		},
		{
			alt: "Sticker die wir haben",
			caption: "Und natürlich auch Sticker",
			src: "/events/stickers2.webp",
		},
		{
			alt: "Sticker die wir haben",
			caption: "Und mehr Sticker",
			src: "/events/sticker.webp",
		},
	];
	// Use the useEffect hook to fetch data when the component mounts
	useEffect(() => {
		const fetchEvents = async () => {
			try {
				// Fetch the JSON data from the URL
				const response = await fetch(eventsUrl);
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				const data = await response.json();

				// Convert timestamps from string to Date objects
				const formattedEvents: EventProps[] = data.map((event: any) => ({
					...event,
					title: event.name,
					description: event.description,
					location: event.entity_metadata?.location || "Online", // Use optional chaining for safety
					startTimestamp: new Date(event.scheduled_start_time).getTime(),
					endTimestamp: new Date(event.scheduled_end_time).getTime(),
					// Derive the type based on the name
					type: event.name.toLowerCase().includes("coding") ? "coding" :
						event.name.toLowerCase().includes("meeting") ? "meeting" :
							"event"
				}));

				// Set the events state with the fetched data, sorted by start time
				setEvents(formattedEvents.sort((a, b) => a.startTimestamp - b.startTimestamp));
			} catch (e) {
				// Set the error state if fetching fails
				if (e instanceof Error) {
					setError(e.message);
				}
			} finally {
				// Set loading to false once the request is complete
				setIsLoading(false);
			}
		};

		fetchEvents();
	}, []); // The empty array ensures this effect runs only once

	return (
		<div>
			<section className="py-20 px-4">
				<div className="container mx-auto text-center max-w-4xl">
					<a href="https://www.sv.tum.de/sv/hochschulgruppen/" target="_blank" rel="noopener noreferrer">
						<Badge
							className="mb-6 px-4 py-2 dark:bg-slate-700 dark:text-gray-300 dark:hover:text-gray-900"
							variant="secondary"
						>
							<MapPin className="w-4 h-4 mr-2" />
							Hochschulgruppe an der Technischen Universität München
						</Badge>
					</a>
					<h1 className="text-5xl md:text-6xl font-bold font-sans dark:text-white text-gray-900 mb-6 leading-tight">
						Open Source
						<span className="dark:bg-linear-to-r dark:from-blue-400 dark:to-blue-500 bg-linear-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">
							{" @ TUM"}
						</span>
					</h1>
					<p className="text-xl dark:text-gray-300 text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
						Wir arbeiten an verschiedenen Open Source Projekten, organisieren
						Veranstaltungen und bieten Workshops an.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Button
							asChild
							className="dark:bg-blue-600 dark:hover:bg-blue-700 text-white bg-blue-600 hover:bg-blue-700"
							size="lg"
						>
							<a href="https://groups.google.com/u/0/a/tum.dev/g/announce/about" target="_blank" rel="noopener noreferrer">
								<Users className="w-5 h-5 mr-2" />
								Für Newsletter Anmelden
							</a>
						</Button>
						<Button
							asChild
							className="dark:hover:bg-slate-600 dark:hover:text-gray-50 dark:text-gray-100 dark:bg-slate-700"
							size="lg"
							variant="outline"
						>
							<Link to="/mitmachen">
								<GithubIcon className="w-5 h-5 mr-2" />
								Unsere Projekte
							</Link>
						</Button>
					</div>
				</div>
			</section>

			{/* Features Section */}
			<section
				className="py-16 px-4 dark:bg-slate-800 bg-gray-50"
				id="wir-machen"
			>
				<div className="container mx-auto max-w-6xl">
					<div className="text-center mb-12">
						<h2 className="text-3xl font-bold dark:text-white text-gray-900 mb-4">
							Was wir machen
						</h2>
						<p className="dark:text-gray-300 text-gray-600 max-w-2xl mx-auto">
							Unsere Projekte sind für alle frei verfügbar und wir freuen uns
							über jede Unterstützung.
						</p>
					</div>

					<div className="grid md:grid-cols-3 gap-8">
						<Card className="border-0 shadow-lg hover:shadow-xl transition-shadow dark:bg-slate-700 dark:border-slate-600 bg-white">
							<CardHeader>
								<div className="w-12 h-12 bg-sky-600 rounded-lg flex items-center justify-center mb-4">
									<Code className="w-6 h-6 text-white" />
								</div>
								<CardTitle className="dark:text-white text-gray-900">
									Software Entwicklung
								</CardTitle>
								<CardDescription className="dark:text-gray-300 text-gray-600">
									Wir entwickeln innovative Open Source Software und tragen zu
									bestehenden Projekten bei.
								</CardDescription>
							</CardHeader>
						</Card>

						<Card className="border-0 shadow-lg hover:shadow-xl transition-shadow dark:bg-slate-700 dark:border-slate-600 bg-white">
							<CardHeader>
								<div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
									<Calendar className="w-6 h-6 text-white" />
								</div>
								<CardTitle className="dark:text-white text-gray-900">
									Veranstaltungen
								</CardTitle>
								<CardDescription className="dark:text-gray-300 text-gray-600">
									Wir organisieren regelmäßige Events, Hackathons und Meetups
									für die Open Source Community.
								</CardDescription>
							</CardHeader>
						</Card>

						<Card className="border-0 shadow-lg hover:shadow-xl transition-shadow dark:bg-slate-700 dark:border-slate-600 bg-white">
							<CardHeader>
								<div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center mb-4">
									<Lightbulb className="w-6 h-6 text-white" />
								</div>
								<CardTitle className="dark:text-white text-gray-900">
									Workshops
								</CardTitle>
								<CardDescription className="dark:text-gray-300 text-gray-600">
									Wir bieten Workshops zu verschiedenen Technologien und Open
									Source Tools an.
								</CardDescription>
							</CardHeader>
						</Card>
					</div>
				</div>
			</section>

			{/* Upcoming Events Section */}
			<section
				className="py-16 px-4 dark:bg-slate-800 bg-gray-50"
				id="events"
			>
				<div className="container mx-auto max-w-6xl">
					<div className="text-center mb-12">
						<h2 className="text-3xl font-bold dark:text-white text-gray-900 mb-4">
							Termine
						</h2>
						<p className="dark:text-gray-300 text-gray-600">
							Verpasse keine unserer Veranstaltungen und Treffen
						</p>
					</div>

					<div className="grid lg:grid-cols-2 gap-8">
						{ eventsSection(isLoading, error, events) }
					</div>
				</div>
			</section>

			{/* Team Section */}
			<section
				className="py-16 px-4 dark:bg-slate-800 bg-gray-50"
				id="team"
			>
				<div className="container mx-auto max-w-6xl">
					<div className="text-center mb-12">
						<h2 className="text-3xl font-bold dark:text-white text-gray-900 mb-4">
							Unser Team
						</h2>
						<p className="dark:text-gray-300 text-gray-600">
							Lerne die Menschen kennen, die unsere Open Source Projekte
							vorantreiben
						</p>
					</div>

					{/* Core Team */}
					<div className="mb-16">
						<h3 className="text-2xl font-semibold dark:text-white text-gray-900 mb-8 text-center">
							Core Team
						</h3>
						<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
							{members
								.filter((m) => m.leads_project)
								.map((member) => (
									<Member
										avatar={member.avatar}
										github={member.github}
										key={member.name + member.avatar}
										leads_project={member.leads_project}
										linkedin={member.linkedin}
										name={member.name}
										role={member.role}
									/>
								))}
						</div>
					</div>

					{/* Members */}
					<div>
						<h3 className="text-2xl font-semibold dark:text-white text-gray-900 mb-8 text-center">
							Aktive Mitglieder
						</h3>
						<div
							className={`grid sm:grid-cols-${Math.min(members.filter((m) => !m.leads_project).length, 2)} md:grid-cols-${Math.min(members.filter((m) => !m.leads_project).length, 3)} lg:grid-cols-${Math.min(members.filter((m) => !m.leads_project).length, 4)} xl:grid-cols-${Math.min(members.filter((m) => !m.leads_project).length, 6)} gap-6`}
						>
							{members
								.filter((m) => !m.leads_project)
								.map((member) => (
									<Member
										avatar={member.avatar}
										key={member.name + member.avatar}
										name={member.name}
										role={member.role}
									/>
								))}
						</div>
					</div>
					{/* Join Team CTA */}
					<div className="mt-16 text-center" id="join">
						<div className="dark:bg-blue-900 bg-blue-50 rounded-lg p-8">
							<h3 className="text-2xl font-bold dark:text-white text-gray-900 mb-4">
								Werde Teil unseres Teams!
							</h3>
							<p className="dark:text-gray-300 text-gray-600 mb-6 max-w-2xl mx-auto">
								Wir suchen immer nach motivierten Entwicklern, Designern und
								Open Source Enthusiasten. Egal ob Anfänger oder Experte - bei
								uns findest du spannende Projekte und ein tolles Team.
							</p>
							<div className="flex flex-col sm:flex-row gap-4 justify-center">
								<Button
									asChild
									className="dark:bg-blue-600 dark:hover:bg-blue-700 bg-blue-600 hover:bg-blue-700 text-white"
								>
									<a href="https://groups.google.com/u/0/a/tum.dev/g/announce/about" target="_blank" rel="noopener noreferrer">
										<Users className="w-4 h-4 mr-2" />
										Auf dem laufenden bleiben
									</a>
								</Button>

								<Button
									asChild
									className="dark:border-slate-500 dark:text-gray-200 dark:hover:bg-slate-700 border-gray-300 text-gray-700 hover:bg-gray-100"
									variant="outline"
								>
									<Link to="/mitmachen">
										<HeartHandshake className="w-4 h-4 mr-2" />
										Hilfe gesucht
									</Link>
								</Button>
							</div>
						</div>
					</div>

					{/* Team Activities Slideshow */}
					<div className="mt-16">
						<div className="text-center mb-8">
							<h3 className="text-2xl font-bold dark:text-white text-gray-900 mb-4">
								Einblicke in unser Team
							</h3>
							<p className="dark:text-gray-300 text-gray-600">
								Von Hackathons bis Server-Aufbau - hier siehst du, was wir
								gemeinsam erleben
							</p>
						</div>

						<Slideshow images={slideshow} />
					</div>
				</div>
			</section>

			{/* CTA Section */}
			<section
				className="py-20 px-4 dark:bg-blue-900 bg-blue-600"
				id="cta"
			>
				<div className="container mx-auto text-center max-w-4xl">
					<h2 className="text-4xl font-bold text-white mb-6">
						Lust mitzumachen?
					</h2>
					<p className="text-xl dark:text-blue-100 text-blue-100 mb-8 leading-relaxed">
						Wenn du Lust hast, bei uns mitzumachen, dann schau doch mal bei
						einem unserer Treffen vorbei. <br />
						Wende dich dafür einfach an uns - wir freuen uns auf dich!
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Button
							asChild
							className="bg-white dark:text-blue-800 text-blue-600 hover:bg-gray-100"
							size="lg"
							variant="secondary"
						>
							<a href="mailto:app@tum.de">
								<Mail className="w-5 h-5 mr-2" />
								Kontakt aufnehmen
							</a>
						</Button>
						<Button
							asChild
							className="border-white text-slate-900 dark:text-slate-50 hover:bg-white/10"
							size="lg"
							variant="outline"
						>
							<Link to="/verein-beitreten">
								<Users className="w-5 h-5 mr-2" />
								Jetzt Mitglied werden
							</Link>
						</Button>
					</div>
				</div>
			</section>
		</div>
	);
}
